import { ListResponseModel, toQueryString } from ".";
import { ProviderCustomerOrder } from "../model/customer/customer";
import { LoanDetails, MandateStatus, NextOrderPayment, CostOverviewDetails, OrderOfferModel, OrderQuery } from "../model/orders/orders";
import { PaymentMethod } from "../model/payment-method";
import { CustomerPaymentHistory } from "../model/payments/payments";
import { SolarProviderCustomerQuery } from "../model/provider/provider-dashboard-data";
import { TransactionType } from "../model/transaction";
import { apiSlice } from "../stores/base/api-slice";

const orderEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getBnplOfferDetails: builder.query<OrderOfferModel, string>({
			query: id => ({
				url: `/orders/bnpl/${id}`,
				method: "GET",
			}),
		}),
		declineBnplOffer: builder.mutation<any, string>({
			invalidatesTags: ["CustomerDashboard"],
			query: id => ({
				url: `/orders/bnpl/decline/${id}`,
				method: "POST",
			}),
		}),
		providerGetOrders: builder.query<ListResponseModel<Array<ProviderCustomerOrder>>, SolarProviderCustomerQuery>({
			providesTags: ["Orders"],
			query: queryString => ({
				url: `/orders/provider` + toQueryString(queryString),
				method: "GET",
			}),
			transformResponse(response: ListResponseModel<Array<ProviderCustomerOrder>>) {
				if (response.items) {
					response.items = response.items.map(x => {
						if (x.paymentDate) {
							x.paymentDate = new Date(x.paymentDate);
						}
						return x;
					});
				}
				return response;
			},
		}),
		updateOrderStatus: builder.mutation<string, any>({
			invalidatesTags: ["Orders"],
			query: req => ({
				url: `/orders/set-new-order-status`,
				method: "POST",
				body: req,
			}),
		}),
		getOrderNextPayment: builder.query<NextOrderPayment, string>({
			query: orderId => ({
				url: `/orders/next-payment/${orderId}`,
				method: "GET",
			}),
		}),
		checkMandateConnected: builder.query<MandateStatus, string>({
			query: orderId => ({
				url: `/orders/mandates/connections?orderId=${orderId}`,
				method: "GET",
			}),
		}),
		getOrderDetails: builder.query<LoanDetails, string>({
			query: orderId => ({
				url: `/orders/${orderId}/details`,
				method: "GET",
			}),
		}),
		getOrderConfirmationDetails: builder.query<
			CostOverviewDetails,
			{
				solarProductId: string;
				roofTypeId: number;
				numberOfFloors: number;
				paymentMethod: PaymentMethod;
				durationOfPaymentsInMonths: number;
			}
		>({
			query: request => ({
				url: `/orders/order-confirmation-details`,
				params: request,
				method: "GET",
			}),
		}),
		getOrderRepaymentDetails: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, OrderQuery>({
			query: query => ({
				url: `/orders/${query?.orderId}/repayments` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		createAnOrder: builder.mutation<
			{
				orderId: string;
			},
			{
				productId: string;
				durationOfPaymentsInMonths?: number;
				roofType?: number;
				numberOfFloors?: number;
				paymentType?: TransactionType;
				deliveryAddressId?: string;
				paystackCardId?: string;
			}
		>({
			query: request => ({
				url: `/orders`,
				method: "POST",
				body: request,
			}),
		}),
		createAnGuestOrder: builder.mutation<
			{
				orderId: string;
				authorizationUrl: string;
				accessCode: string;
				reference: string;
			},
			{
				productId: string;
				firstName: string;
				lastName: string;
				phoneNumber?: string;
				additionalPhoneNumber?: string;
				email: string;
				additionalInformation?: string;
				roofType?: number;
				numberOfFloors?: number;
				installationAddress: string;
				redirectUrl: string;
				failedRedirectUrl: string;
			}
		>({
			query: request => ({
				url: `/orders/guest-order`,
				method: "POST",
				body: request,
			}),
		}),
		getPaymentConfirmationDetails: builder.mutation<
			CostOverviewDetails,
			{
				orderId: string;
			}
		>({
			query: req => ({
				url: `/orders/payment-confirmation-details?orderId=${req.orderId}`,
				method: "POST",
			}),
		}),
		getGuestOrderInfo: builder.query<
			{
				productName: string;
				customerFullName: string;
				customerEmail: string;
				customerPhoneNumber: string;
				orderNumber: string;
				dateOfPurchase: string;
				amountPaid: number;
				orderStatus: string;
			},
			string
		>({
			query: id => ({
				url: `/orders/guest/${id}`,
				method: "GET",
			}),
		}),
	}),
});

export const {
	useLazyGetBnplOfferDetailsQuery,
	useDeclineBnplOfferMutation,
	useLazyProviderGetOrdersQuery,
	useLazyGetOrderConfirmationDetailsQuery,
	useUpdateOrderStatusMutation,
	useLazyGetOrderNextPaymentQuery,
	useLazyCheckMandateConnectedQuery,
	useLazyGetOrderDetailsQuery,
	useLazyGetOrderRepaymentDetailsQuery,
	useCreateAnOrderMutation,
	useGetPaymentConfirmationDetailsMutation,
	useCreateAnGuestOrderMutation,
	useLazyGetGuestOrderInfoQuery,
} = orderEndpoints;
